import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { getSearchParams, getParams } from "../../../utlis/helpers";
import { ACTIVITY_MONITORING } from "../../../routes/routerPath";
import {
  getActivityMonitorData,
  getActivityTrendData,
} from "../../../services/activityMonitoringApi";
import { userInfoFetch, formatName } from "../../../utlis/commonFunction";
import { getCurrentLocale } from "../../../utlis/locale";
import { getSensorType } from "./sensorTypes";
import { useT } from "../../../i18n/useT";
import "./activitytrend.scss";

const toLocalDate = (date) =>
  [date.getFullYear(), date.getMonth() + 1, date.getDate()]
    .map((part, index) => String(part).padStart(index === 0 ? 4 : 2, "0"))
    .join("-");

const getDefaultTrendRange = () => {
  const to = new Date();
  const from = new Date(to.getFullYear(), to.getMonth(), to.getDate() - 41);
  return { from: toLocalDate(from), to: toLocalDate(to) };
};

/**
 * Standalone Activity Trend screen. Kept as a separate route from
 * ActivityMonitoring; loads sensor metadata from the normalized monitor payload
 * and the latest six weeks from the dedicated resident trend endpoint.
 */
const ActivityTrend = () => {
  const t = useT("activityMonitoring");
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Stable reference — userInfoFetch() returns a fresh object each call, which
  // would otherwise churn the memoized callback and re-trigger the fetch effect.
  const [userInfo] = useState(() => userInfoFetch());
  const { seniorId: seniorIdFromParams } = getParams();
  const seniorUuid = seniorIdFromParams || userInfo?.id;

  const withResident = useCallback(
    (payload) => {
      if (!payload) return payload;
      const name = formatName(userInfo, getCurrentLocale());
      return {
        ...payload,
        resident: {
          ...payload.resident,
          ...(name ? { name } : {}),
          ...(userInfo?.gender ? { gender: userInfo.gender } : {}),
          ...(userInfo?.age ? { age: userInfo.age } : {}),
          ...(userInfo?.room ? { location: userInfo.room } : {}),
        },
      };
    },
    [userInfo],
  );

  const load = useCallback(async () => {
    if (!seniorUuid) {
      setError(t("senior_missing"));
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const range = getDefaultTrendRange();
      const [monitorResponse, trendResponse] = await Promise.all([
        getActivityMonitorData(seniorUuid),
        getActivityTrendData(seniorUuid, range.from, range.to),
      ]);
      const monitor = monitorResponse?.data || monitorResponse;
      const trend = trendResponse?.data || trendResponse;
      if (!monitor?.resident || !getSensorType(monitor.sensor?.type)) {
        throw new Error("Unable to load activity trend for this sensor.");
      }
      setData(withResident({ ...monitor, trend }));
    } catch (err) {
      console.error("Activity trend load failed.", err);
      setData(null);
      // Backend messages arrive localized via Accept-Language; client-side
      // failures get the translated generic message.
      setError(err.response?.data?.message || t("trend_load_failed"));
    } finally {
      setIsLoading(false);
    }
  }, [seniorUuid, withResident, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleBack = () => {
    navigate(ACTIVITY_MONITORING + getSearchParams());
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress size={32} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="activity-trend-wrapper">
        <div className="error-container">
          <div className="error-text">{error || t("no_trend_data")}</div>
          <button className="btn btn-primary" onClick={load}>
            {t("retry")}
          </button>
        </div>
      </div>
    );
  }

  const sensorType = getSensorType(data?.sensor?.type);
  const TrendView = sensorType.TrendView;
  return <TrendView data={data} logic={sensorType.logic} onBack={handleBack} />;
};

export default ActivityTrend;
